/**
 * ScreenPreviewDrawer — Right context drawer for article preview during screening
 * 
 * Shows:
 * - Abstract / short summary
 * - Metadata (journal, date, authors)
 * - Open source button
 * - Same triage actions (Save to Library, Skip)
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../ui/sheet';
import { ScrollArea } from '../../ui/scroll-area';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import { ScreenQueueArticle } from '../../../api';
import {
  BookmarkPlus,
  BookmarkCheck,
  Clock,
  SkipForward,
  ExternalLink,
  Calendar,
  Building,
  Users,
  Loader2,
  GraduationCap,
} from 'lucide-react';

interface ScreenPreviewDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  article: ScreenQueueArticle | null;
  isProcessing: boolean;
  onSave: () => void;
  onDefer: () => void;
  onSkip: () => void;
  onOpenInLearn: () => void;
}

export const ScreenPreviewDrawer: React.FC<ScreenPreviewDrawerProps> = ({
  open,
  onOpenChange,
  article,
  isProcessing,
  onSave,
  onDefer,
  onSkip,
  onOpenInLearn,
}) => {
  if (!article) return null;

  const formattedDate = article.pub_date
    ? new Date(article.pub_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const isSaved = article.screening_status === 'keep' || article.is_in_library;
  const isDeferred = article.screening_status === 'later';
  const isSkipped = article.screening_status === 'skip';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[420px] lg:w-[480px] p-0 flex flex-col"
        data-testid="screen-preview-drawer"
      >
        {/* Header */}
        <SheetHeader className="p-4 border-b border-[hsl(var(--border))] flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="responsive-title text-base font-semibold leading-snug line-clamp-3">
                {article.title}
              </SheetTitle>
              <SheetDescription className="mt-2 space-y-1">
                {article.journal && (
                  <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--primary))]">
                    <Building className="w-3.5 h-3.5" />
                    <span>{article.journal}</span>
                  </div>
                )}
                {formattedDate && (
                  <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{formattedDate}</span>
                  </div>
                )}
              </SheetDescription>
            </div>

            {/* Status badge */}
            {(isSaved || isDeferred || isSkipped) && (
              <Badge
                className={`flex-shrink-0 ${
                  isSaved
                    ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                    : isDeferred
                    ? 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30'
                    : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
                }`}
                variant="outline"
              >
                {isSaved && <BookmarkCheck className="w-3 h-3 mr-1" />}
                {isDeferred && <Clock className="w-3 h-3 mr-1" />}
                {isSkipped && <SkipForward className="w-3 h-3 mr-1" />}
                {isSaved ? 'Saved' : isDeferred ? 'Deferred' : 'Skipped'}
              </Badge>
            )}
          </div>

          {/* Tags */}
          {article.design_tags && article.design_tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {article.design_tags.map((tag, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            {!isSaved && !isSkipped && (
              <>
                <Button
                  onClick={onSave}
                  disabled={isProcessing}
                  className="flex-1 bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/80 text-[hsl(var(--background))]"
                  data-testid="preview-save"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                  )}
                  Save to Library
                </Button>
                <Button
                  variant="outline"
                  onClick={onSkip}
                  disabled={isProcessing}
                  data-testid="preview-skip"
                >
                  <SkipForward className="w-4 h-4 mr-2" />
                  Skip
                </Button>
              </>
            )}

            {isSaved && (
              <Button
                variant="outline"
                className="flex-1 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
                onClick={onOpenInLearn}
                data-testid="preview-open-learn"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Open in Learn
              </Button>
            )}

            {isDeferred && !isSaved && (
              <Button
                onClick={onSave}
                disabled={isProcessing}
                className="flex-1 bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/80 text-[hsl(var(--background))]"
                data-testid="preview-save-deferred"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                )}
                Save to Library
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`, '_blank')}
              data-testid="preview-pubmed"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              PubMed
            </Button>
          </div>
        </SheetHeader>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* AI Summary */}
            {article.ai_summary && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  AI Summary
                </h4>
                <div className="bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20 rounded-lg p-3">
                  <p className="text-sm text-[hsl(var(--foreground))] leading-relaxed">
                    {article.ai_summary}
                  </p>
                </div>
              </div>
            )}

            {/* Abstract */}
            {article.abstract && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  Abstract
                </h4>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                  {article.abstract}
                </p>
              </div>
            )}

            {/* Authors */}
            {article.authors && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  Authors
                </h4>
                <div className="flex items-start gap-1.5">
                  <Users className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {article.authors}
                  </p>
                </div>
              </div>
            )}

            {/* Topic tags */}
            {article.topic_tags && article.topic_tags.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  Topics
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {article.topic_tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* PMID */}
            <div>
              <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                PubMed ID
              </h4>
              <code className="text-sm text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-1 rounded">
                {article.pmid}
              </code>
            </div>
          </div>
        </ScrollArea>

        {/* Keyboard shortcuts hint */}
        <div className="p-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 flex-shrink-0">
          <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center">
            <kbd className="px-1 rounded bg-[hsl(var(--muted))]">S</kbd> Save 
            <kbd className="px-1 rounded bg-[hsl(var(--muted))] ml-2">X</kbd> Skip 
            <kbd className="px-1 rounded bg-[hsl(var(--muted))] ml-2">O</kbd> Open PubMed
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ScreenPreviewDrawer;
